/**
 * Wire protocol.
 *
 * Control traffic (join / lobby / commands / diplomacy) is JSON — it is
 * low frequency and easy to debug. Hot state (territory, entities, player
 * stats) is hand-rolled binary with varints, because at 100 players it is the
 * only thing that matters for bandwidth.
 */

/* ------------------------------------------------------------ binary codec */

export class BinWriter {
  private buf: Uint8Array;
  private view: DataView;
  private pos = 0;

  constructor(initial = 2048) {
    this.buf = new Uint8Array(initial);
    this.view = new DataView(this.buf.buffer);
  }

  private ensure(n: number) {
    if (this.pos + n <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < this.pos + n) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf);
    this.buf = next;
    this.view = new DataView(this.buf.buffer);
  }

  u8(v: number) { this.ensure(1); this.buf[this.pos++] = v & 255; return this; }
  u16(v: number) { this.ensure(2); this.view.setUint16(this.pos, v & 0xffff, true); this.pos += 2; return this; }
  u32(v: number) { this.ensure(4); this.view.setUint32(this.pos, v >>> 0, true); this.pos += 4; return this; }
  i16(v: number) { this.ensure(2); this.view.setInt16(this.pos, v | 0, true); this.pos += 2; return this; }
  f32(v: number) { this.ensure(4); this.view.setFloat32(this.pos, v, true); this.pos += 4; return this; }

  /** Unsigned LEB128. Values are clamped to >= 0. */
  varint(v: number) {
    let x = v < 0 ? 0 : Math.floor(v);
    this.ensure(5);
    while (x >= 0x80) {
      this.buf[this.pos++] = (x & 0x7f) | 0x80;
      x = Math.floor(x / 128);
    }
    this.buf[this.pos++] = x & 0x7f;
    return this;
  }

  /** Append a raw byte range (used to splice pre-encoded sub-blocks). */
  write(src: Uint8Array) {
    this.ensure(src.length);
    this.buf.set(src, this.pos);
    this.pos += src.length;
    return this;
  }

  get length() { return this.pos; }

  /** Copy of the written bytes (safe to hand to ws.send / queue). */
  bytes(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }
}

export class BinReader {
  private view: DataView;
  private buf: Uint8Array;
  private pos = 0;

  constructor(data: ArrayBuffer | Uint8Array) {
    this.buf = data instanceof Uint8Array ? data : new Uint8Array(data);
    this.view = new DataView(this.buf.buffer, this.buf.byteOffset, this.buf.byteLength);
  }

  get eof() { return this.pos >= this.buf.length; }

  u8() { return this.buf[this.pos++]; }
  u16() { const v = this.view.getUint16(this.pos, true); this.pos += 2; return v; }
  u32() { const v = this.view.getUint32(this.pos, true); this.pos += 4; return v; }
  i16() { const v = this.view.getInt16(this.pos, true); this.pos += 2; return v; }
  f32() { const v = this.view.getFloat32(this.pos, true); this.pos += 4; return v; }

  varint() {
    let result = 0, shift = 1;
    for (;;) {
      const b = this.buf[this.pos++];
      result += (b & 0x7f) * shift;
      if ((b & 0x80) === 0) break;
      shift *= 128;
    }
    return result;
  }
}

/** Positions travel as tile coordinates in 1/16th steps (max 4096 tiles). */
export const POS_SCALE = 16;
export const packPos = (v: number) => Math.max(0, Math.min(65535, Math.round(v * POS_SCALE)));
export const unpackPos = (v: number) => v / POS_SCALE;

/* ------------------------------------------------------- JSON: client -> server */

export type ClientMsg =
  | {
    t: 'hello'; name: string; room?: string; create?: boolean; race?: number; version: number;
    /**
     * A random string this browser keeps to itself, and the **only** key to a
     * kingdom that was left sleeping. Not an account and not a claim about who
     * anybody is: it says only "the same browser as before". Reconnection used
     * to match on the player's *name*, which is public, printed across the map,
     * and — since duplicates are allowed — not even unique.
     */
    token?: string;
    /**
     * Which edition Quickplay should look in when no room code was given.
     * Ignored for a coded join — the world already knows what it is.
     */
    light?: boolean;
    /** only read when `create` is set: the settings the host chose for their world */
    opts?: {
      mode?: number; maxPlayers?: number; teams?: number; aiKingdoms?: number; camps?: number;
      light?: boolean;
    };
    /**
     * **Which front door this player came through** — `'crazygames'` from the
     * portal build, absent from the site's own.
     *
     * The funnel files every step under it (`join|src=crazygames`), which is the
     * only way to answer "did the portal's traffic actually stay" separately
     * from our own. Without it the two are added together and the dashboard's
     * "% of arrivals" is nonsense, because arrivals are counted by our server
     * serving the page and a portal visitor never asks us for one.
     *
     * **Not an identifier.** It is the same constant string for every player in
     * that build, carries nothing about the person, and is why this needed no
     * privacy-policy change. Absent means `web`, so an older client is counted
     * correctly rather than specially.
     */
    src?: string;
  }
  | { t: 'race'; race: number }
  /**
   * Picking a colour in a team lobby: -1 puts you back on automatic.
   *
   * **Honoured only in a private room** — one somebody opened by hand, which the
   * server knows as `Room.manualStart` and the lobby screen reads off
   * `LobbyInfo.manualStart`. A public world balances its teams itself and
   * ignores this message entirely, so the buttons are hidden there; sending it
   * anyway does nothing at all. See the `team` case in `server/room.ts`.
   */
  | { t: 'team'; team: number }
  | { t: 'spawn'; tile: number }
  | { t: 'attack'; tile: number; ratio: number }
  | { t: 'build'; s: number; tile: number }
  /** one mouse-drag's worth of wall: the tiles it crossed, and what to lay */
  | { t: 'wall'; tiles: number[]; gate: boolean }
  /** the standing garrison: hold the contested frontier, and follow it as it moves */
  | { t: 'defend'; on: boolean }
  /** the standing repair order: masons mend what is damaged, paid in gold */
  | { t: 'repair'; on: boolean }
  /**
   * Fix one of your Markets on a trade partner. `target` is another market's
   * structure id, or anything below zero to hand the route back to the
   * merchant's own judgement — which is the default.
   */
  | { t: 'trade'; id: number; target: number }
  /** your people's one devastating ability: where, and how wide a bite */
  | { t: 'power'; x: number; y: number; r: number }
  | { t: 'train'; u: number; x: number; y: number }
  | { t: 'boat'; tile: number; ratio: number }
  /** lay down a warship at your nearest Harbour and send it to this water */
  | { t: 'warship'; tile: number }
  | { t: 'move'; ids: number[]; x: number; y: number }
  /**
   * Send this squad after that unit — a caravan on the road, a patrol passing.
   *
   * **This declares war.** It is the second way to open one (the first being an
   * attack on territory), and it exists because a kingdom you share no border
   * with used to be untouchable no matter what it drove past your fields.
   */
  | { t: 'raid'; ids: number[]; target: number }
  /**
   * Put this squad on that wall. The same declaration as a raid, aimed at
   * stonework — the only answer to a border a neighbour closed against a realm
   * they were never at war with.
   */
  | { t: 'siege'; ids: number[]; tile: number }
  | { t: 'sail'; ids: number[]; tile: number }
  | { t: 'disband'; ids: number[] }
  /** call off the attack running against this realm; 0 is the one on wilderness */
  | { t: 'cancel'; target: number }
  | { t: 'spare'; ids: number[]; spare: boolean }
  /** the host of a private room calling the war to order */
  | { t: 'start' }
  | { t: 'meteor'; id: number; x: number; y: number }
  | { t: 'gift'; to: number; r: number; amount: number }
  | { t: 'aid'; to: number; amounts: number[] }
  | { t: 'reinforce'; to: number; amount: number }
  | { t: 'ping'; x: number; y: number; kind: number }
  /**
   * Where the camera is, and — in `ids` — which kingdoms are on that screen.
   *
   * The client is the only party that cheaply knows the second one: it holds
   * the ownership map and paints it every frame, where the server would have to
   * sweep tens of thousands of tiles per client to work it out. Whoever is
   * under the cursor is in the list too, so a hover over a distant province
   * still fills the strip. Nothing secret rides on it — see `encodePlayers`.
   */
  | { t: 'view'; x0: number; y0: number; x1: number; y1: number; ids?: number[] }
  | { t: 'pong'; ts: number };

/* ------------------------------------------------------- JSON: server -> client */

export interface PlayerInfo {
  id: number;
  name: string;
  team: number;
  color: number;
  bot: boolean;
  kind: number;   // PlayerKind: 0 human, 1 AI kingdom, 2 monster camp
  race: number;   // Race: 0 human, 1 dwarf, 2 elf
}

export interface LobbyInfo {
  humans: number;
  max: number;
  teams: number;
  perTeam: number;
  aiKingdoms: number;
  camps: number;
  /** RoomMode: 0 free-for-all, 1 teams, 2 VS AI */
  mode: number;
  modeName: string;
  /**
   * The **Light** edition — humans only, no mana, one barracks. The two never
   * share a world, so the browser shows you the rooms of the edition you are
   * in and nothing else.
   */
  light: boolean;
  /** the world this match will be played on, sized to the roster */
  worldW: number;
  worldH: number;
  /**
   * **This room is private** — somebody opened it by hand rather than it being
   * one of the rotating public worlds.
   *
   * Two things hang off it. A private room has no countdown: its host starts it
   * by hand. And a private room is the only place you may **choose your team** —
   * a public world splits its sides evenly and the server ignores the `team`
   * message there, so the lobby screen must hide the buttons when this is false
   * rather than offer a control that silently does nothing.
   */
  manualStart: boolean;
  /** which player may press Start. 0 = nobody yet */
  hostId: number;
}

export type ServerMsg =
  | { t: 'welcome'; you: number; room: string; version: number }
  | { t: 'lobby'; phase: number; countdown: number; players: PlayerInfo[]; teams: string[]; info: LobbyInfo }
  | {
    t: 'match'; seed: number; worldW: number; worldH: number;
    phase: number; countdown: number; players: PlayerInfo[]; you: number;
  }
  | { t: 'phase'; phase: number; countdown: number }
  | { t: 'joined'; player: PlayerInfo }
  | { t: 'left'; id: number }
  | { t: 'notice'; text: string; kind?: string }
  | { t: 'ping'; from: number; x: number; y: number; kind: number; color: number }
  /**
   * Every kingdom this player has come to blows with, pushed when the list
   * grows. It never shrinks — a grudge outlives a battle — so the client can
   * simply replace its copy. Drives the crossed swords on the map, and it is
   * the rule soldiers obey: they engage nobody who is not on this list.
   */
  | { t: 'war'; ids: number[] }
  | { t: 'gameover'; standings: { team: number; tiles: number; name: string }[]; winner: number }
  /**
   * "Not here — hang up and dial again."
   *
   * A busy server is one process per core sharing a port, and a room lives in
   * exactly one of them. The operating system hands each new connection to the
   * next core in turn, so dialling again walks round the machine and finds the
   * one that owns the code within a few tries. Running out of them means the
   * code was wrong, not that the server is broken.
   */
  | { t: 'retry-room'; room: string; shard: number }
  | { t: 'ping-rtt'; ts: number };
